import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const links = [
    { href: "#about", label: "About" },
    { href: "#projects", label: "Projects" },
    { href: "#experience", label: "Experience" },
    { href: "#skills", label: "Skills" },
    { href: "#education", label: "Education" },
    { href: "/blog", label: "Blog" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <motion.nav
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 w-full z-50
        flex items-center justify-between px-6 py-3 sm:py-4
        transition-all duration-300
        ${
          scrolled
            ? "backdrop-blur-md bg-black/30 border-b border-white/5"
            : "bg-transparent"
        }`}
    >
      {/* === Left: NeuralBrew Brand === */}
      <Link
        to="/"
        onClick={() => {
          // If already on home, scroll smoothly to top
          if (window.location.pathname === "/") {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }}
        className="flex items-center gap-3 group cursor-pointer"
      >
        <img
          src="/neuralbrew.png"
          alt="NeuralBrew Logo"
          className="w-8 sm:w-9 drop-shadow-[0_0_10px_rgba(0,224,183,0.25)]
                     group-hover:drop-shadow-[0_0_15px_rgba(0,224,183,0.4)]
                     transition-all duration-300"
        />
        <span
          className="text-xl sm:text-[1.35rem] font-bold
            bg-gradient-to-r from-[#00e0b7] via-[#4b5fff] to-[#b45eff]
            bg-clip-text text-transparent
            drop-shadow-[0_0_6px_rgba(75,95,255,0.3)] tracking-tight"
        >
          NeuralBrew
        </span>
      </Link>

      {/* === Right: Menu Button === */}
      <div className="relative">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center justify-center p-3 rounded-full
                     bg-white/[0.04] border border-white/10
                     shadow-[0_0_10px_rgba(0,224,183,0.25)]
                     text-slate-300 hover:text-white
                     hover:shadow-[0_0_15px_rgba(0,224,183,0.4)]
                     transition-all"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>

        {/* === Dropdown === */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 10 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="absolute right-0 mt-3 w-48
                         bg-black/80 backdrop-blur-xl
                         border border-white/10 rounded-2xl
                         p-4 flex flex-col items-center space-y-3
                         shadow-[0_0_25px_rgba(0,224,183,0.3)]"
            >
              {links.map((link) =>
                link.href.startsWith("/") ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setOpen(false)}
                    className="text-sm font-semibold
                      bg-gradient-to-r from-[#00e0b7] via-[#4b5fff] to-[#b45eff]
                      bg-clip-text text-transparent hover:opacity-90
                      transition-all duration-300"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setOpen(false)}
                    className="text-sm font-semibold
                      bg-gradient-to-r from-[#00e0b7] via-[#4b5fff] to-[#b45eff]
                      bg-clip-text text-transparent hover:opacity-90
                      transition-all duration-300"
                  >
                    {link.label}
                  </a>
                )
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
